import React from 'react';
import { useIntl } from 'react-intl-next';

import {
	AnalyticsListener,
	type UIAnalyticsEvent,
	withAnalyticsEvents,
	type CreateUIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlaskit/analytics-next';
import Avatar from '@atlaskit/avatar';
import AvatarGroup from '@atlaskit/avatar-group';
import ProfileCardTrigger from '@atlaskit/profilecard/user';
import ProfileClient from '@atlaskit/profilecard/client';
import Tooltip from '@atlaskit/tooltip';

import { fireUIAnalytics, fireOperationalAnalytics } from '@atlassian/analytics-bridge';

import { ContributorsContainer } from '../styled';
import messages from '../messages';

interface AvatarWithProfileCardProps {
	userId: string;
	username: string;
	cloudId?: string;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
}

const openUserProfileWindow = (userId: string) => window.open(`/wiki/display/~${userId}`, '_blank');

const DIRECTORY_URL = '/gateway/api/directory/graphql';

const AvatarWithProfileCardBase = (props: AvatarWithProfileCardProps) => {
	const { userId, cloudId, createAnalyticsEvent } = props;
	const { formatMessage } = useIntl();
	const profileClient = new ProfileClient({
		url: DIRECTORY_URL,
	});

	return (
		<AnalyticsListener
			onEvent={(analyticsEvent: UIAnalyticsEvent) => {
				if (createAnalyticsEvent) {
					const { action, actionSubject, actionSubjectId } = analyticsEvent.payload;

					if (
						action === 'rendered' &&
						actionSubject === 'profilecard' &&
						actionSubjectId === 'content'
					) {
						fireOperationalAnalytics(
							createAnalyticsEvent({}),
							'confluencePageTreeAvatar profileCardView',
						);
					}
				}
			}}
			channel="peopleTeams"
		>
			<ProfileCardTrigger
				cloudId={cloudId}
				userId={userId}
				resourceClient={profileClient}
				actions={[
					{
						label: formatMessage(messages.viewProfile),
						callback: () => {
							openUserProfileWindow(userId);
						},
					},
				]}
				prepopulatedData={{
					fullName: props.username,
				}}
			>
				<Avatar
					{...props}
					onClick={(_, analyticsEvent?: UIAnalyticsEvent) => {
						if (analyticsEvent) {
							fireUIAnalytics(analyticsEvent, 'confluencePageTreeAvatar', {
								dropdown: false,
							});
						}
					}}
				/>
			</ProfileCardTrigger>
		</AnalyticsListener>
	);
};

const AvatarWithProfileCard: React.ForwardRefExoticComponent<AvatarWithProfileCardProps> =
	withAnalyticsEvents()(AvatarWithProfileCardBase);

interface ContributorsProps {
	cloudId: string | null;
	contributors?: {
		publishers?: {
			users: any[];
		};
	};
	testId?: string;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	isAvatarTooltipDisabled?: boolean;
}

const Contributors = ({
	cloudId,
	contributors,
	testId,
	isAvatarTooltipDisabled,
}: ContributorsProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (!cloudId || !contributors || !contributors.publishers || !contributors.publishers.users) {
		if (createAnalyticsEvent) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'confluencePageTree unknownContributor');
		}

		const couldNotLoadContMessage = formatMessage(messages.couldNotLoadContributors);

		return (
			<ContributorsContainer>
				<Tooltip content={couldNotLoadContMessage}>
					<Avatar size="small" name={couldNotLoadContMessage} label={couldNotLoadContMessage} />
				</Tooltip>
			</ContributorsContainer>
		);
	}

	const data: any[] = contributors.publishers.users.map((user) => ({
		name: user.displayName,
		size: 'medium',
		src: user.profilePicture.path,
		userId: user.accountId,
	}));

	const AvatarWithCloudId = (avatarProps: any) => (
		<AvatarWithProfileCard {...avatarProps} cloudId={cloudId} />
	);

	const handleAvatarClicked = (_event: React.MouseEvent, _analyticsEvent: any, index: number) => {
		if (createAnalyticsEvent) {
			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'avatar',
			});
			fireUIAnalytics(event, 'confluencePageTreeAvatar', {
				dropdown: true,
			});
		}
		openUserProfileWindow(data[index].userId);
	};

	return (
		<ContributorsContainer>
			<AvatarGroup
				appearance="stack"
				data={data}
				avatar={AvatarWithCloudId}
				size="small"
				maxCount={3}
				onAvatarClick={handleAvatarClicked}
				testId={testId && `${testId}--avatargroup`}
				isTooltipDisabled={isAvatarTooltipDisabled}
			/>
		</ContributorsContainer>
	);
};

export { Contributors, AvatarWithProfileCard, AvatarWithProfileCardBase };
